import { z } from 'zod'
import { AsyncFn } from '../../common'
import { guidSchema } from '../../contracts/_common'
import { AccountGuid } from '../Accounts/Account'
import { CompanyGuid, ForCompanyUser } from '../Company/Company'
import { bzOptional, Guid } from '../common-schemas'
import { JobGuid } from '../Job'
import { Location, UpsertLocationDTOSchema } from '../Locations/Location'
import { MaintenancePlanGuid } from '../MaintenancePlans/MaintenancePlanTypes'

export type AccountLocation = {
  accountGuid: AccountGuid
  companyGuid: CompanyGuid
  location: Location
  isArchived: boolean
  maintenancePlanGuids?: MaintenancePlanGuid[]
  maintenancePlanVisits?: {
    maintenancePlanGuid: MaintenancePlanGuid
    maintenancePlanVisitGuid: string
    jobGuid?: JobGuid
  }[]
}

export type AccountLocationWithoutMaintenancePlans = Omit<
  AccountLocation,
  'maintenancePlanGuids' | 'maintenancePlanVisits'
>

export const UpsertAccountLocationDTOSchema = z.object({
  accountGuid: guidSchema,
  location: z.union([UpsertLocationDTOSchema, guidSchema]),
  isArchived: z.boolean(),
  skipAddressValidation: bzOptional(z.boolean()),
})

export type UpsertAccountLocationDTO = z.infer<typeof UpsertAccountLocationDTOSchema>

export type IAccountLocationUpserter = AsyncFn<ForCompanyUser<UpsertAccountLocationDTO>>

export type AccountLocationPair = {
  readonly accountGuid: AccountGuid
  readonly locationGuid: Guid
}

/**
 * Contract payload for toggling the archive state on an account-location link.
 *
 * @remarks
 * - Must always be executed within a {@link ForCompanyUser} envelope to guarantee tenant isolation.
 * - When `isArchived` is set to `true`, the implementation must enforce validation rules that block
 *   archiving locations with active maintenance plans or open jobs.
 * - Successful execution is expected to update persistence and coordinate search index updates so
 *   archived locations disappear from discovery surfaces by default.
 */
export const AccountLocationArchiveToggleDtoSchema = z.object({
  accountGuid: guidSchema,
  locationGuid: guidSchema,
  isArchived: z.boolean(),
})

export type AccountLocationArchiveToggleDto = z.infer<typeof AccountLocationArchiveToggleDtoSchema>

export enum LocationArchiveBlockReason {
  ACTIVE_MAINTENANCE_PLAN = 'LOCATION_ARCHIVE_BLOCKED_ACTIVE_MAINTENANCE_PLAN',
  OPEN_JOBS = 'LOCATION_ARCHIVE_BLOCKED_OPEN_JOBS',
}

/**
 * Service abstraction responsible for validating and applying archive state transitions on
 * account-location links.
 *
 * Implementations are required to:
 * - Validate archive preconditions using the provided company scoped context.
 * - Flip the persisted archive flag on the account-location row.
 * - Initiate the necessary search index updates so archived entities are hidden across discovery
 *   surfaces.
 */
export type IAccountLocationArchiveToggler = AsyncFn<ForCompanyUser<AccountLocationArchiveToggleDto>>
